import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Typography, IconButton, Chip, Stack, CircularProgress, FormHelperText,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import StarIcon from '@mui/icons-material/Star';
import AppService from '../../services/api';
import WDButton from '../../MaterialComponents/WDButton';
import { DOCUMENT_TYPE_MAPPING } from '../../Configurations/StatFieldData';
import { API_ENDPOINTS } from '../../Api/endpoints';
import { getAuthToken } from '../../utils/authUtils';

const FILE_UPLOAD_ENDPOINT = '/v1/files/upload';

// Non-cryptographic id for React keys / temp file rows (Date/Math available in the browser).
const genId = () => `${Date.now()}_${Math.random().toString(36).slice(2)}`;

// Resolve the selected employee's UID from the shared form context (same sources WorkflowFileField uses).
const resolveEmployeeUid = () => {
  const ctx = window.currentFormData || {};
  return (
    ctx.employeeUid ||
    (ctx.selectedEmployee && (ctx.selectedEmployee.uid || ctx.selectedEmployee.employeeId)) ||
    (window.selectedEmployee && (window.selectedEmployee.uid || window.selectedEmployee.employeeId)) ||
    window.currentEmployeeUid ||
    window.currentEmployeeId ||
    null
  );
};

/**
 * Multiple-instance certificate field (educational / experience certificates).
 *
 * A field of this type can hold several titled certificates, each with multiple files. This popup
 * collects them; the files are uploaded to S3 immediately (for preview), and the value is stored as
 * a { isMultiCertificate, subs: [{ title, files }] } object. At workflow-request submit the parent's
 * write-back bulk-saves each titled sub to the employee's document profile (one group per title) and
 * references them on the ticket. `existingRefs` carries any auto-filled certificates already on the
 * profile so they aren't dropped when the user adds more here.
 */
const MultiCertificateField = ({ field, value, onChange, disabled }) => {
  const fieldCode = field.code || field.name;
  const backendType = DOCUMENT_TYPE_MAPPING[fieldCode] ||
    (`${fieldCode}`.toLowerCase().includes('experience') ? 'experience_certificate' : 'educational_certificate');
  const label = field.label || field.title || fieldCode;

  const [open, setOpen] = useState(false);
  const [subs, setSubs] = useState([]);
  const [existingRefs, setExistingRefs] = useState([]);
  const [error, setError] = useState(null);

  // File-type fields can be stored nested as { target: { value } } by the parent's change handler
  // (auto-fill stores top-level). Unwrap so this component reads the value the same either way.
  const val = (value && typeof value === 'object' && value.target && typeof value.target === 'object' && 'value' in value.target)
    ? value.target.value : value;

  // Flat list of the field's current documents (no titles) — auto-filled refs and/or files added in
  // the popup — so the field shows the actual files like a single-instance document field does.
  const displayDocs = (() => {
    if (val && val.isReference && Array.isArray(val.documentReferences)) return val.documentReferences;
    if (val && val.isMultiCertificate) {
      const ex = Array.isArray(val.existingRefs) ? val.existingRefs : [];
      const subFiles = Array.isArray(val.subs) ? val.subs.flatMap((s) => (s.files || [])) : [];
      return [...ex, ...subFiles];
    }
    return [];
  })();

  const viewDoc = async (filePath, fileName) => {
    if (!filePath) return;
    try {
      const url = API_ENDPOINTS.FILES.DOWNLOAD(filePath);
      const token = getAuthToken && getAuthToken();
      const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!res.ok) throw new Error(`View failed: ${res.status}`);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      window.open(objectUrl, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(objectUrl), 30000);
    } catch (e) {
      setError(`Failed to open ${fileName || 'document'}.`);
    }
  };

  const openDialog = () => {
    if (val && val.isMultiCertificate && Array.isArray(val.subs)) {
      setSubs(val.subs.map((s) => ({ id: genId(), title: s.title || '', files: (s.files || []).map((f) => ({ ...f, id: genId() })) })));
      setExistingRefs(Array.isArray(val.existingRefs) ? val.existingRefs : []);
    } else if (val && val.isReference && Array.isArray(val.documentReferences)) {
      setSubs([{ id: genId(), title: '', files: [] }]);
      setExistingRefs(val.documentReferences);
    } else {
      setSubs([{ id: genId(), title: '', files: [] }]);
      setExistingRefs([]);
    }
    setError(null);
    setOpen(true);
  };

  const addSub = () => setSubs((prev) => [...prev, { id: genId(), title: '', files: [] }]);
  const removeSub = (id) => setSubs((prev) => prev.filter((s) => s.id !== id));
  const setSubTitle = (id, title) => setSubs((prev) => prev.map((s) => (s.id === id ? { ...s, title } : s)));
  const removeFile = (subId, fileId) => setSubs((prev) => prev.map((s) => (s.id === subId ? { ...s, files: s.files.filter((f) => f.id !== fileId) } : s)));

  const onSubFiles = async (subId, fileList) => {
    const uid = resolveEmployeeUid();
    if (!uid) { setError('No employee selected — cannot upload certificate files.'); return; }
    const files = Array.from(fileList || []);
    for (const file of files) {
      const tempId = genId();
      setSubs((prev) => prev.map((s) => (s.id === subId ? { ...s, files: [...s.files, { id: tempId, fileName: file.name, uploading: true }] } : s)));
      try {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('fileType', 'USER_DOCUMENT');
        fd.append('fileName', file.name);
        fd.append('documentType', backendType);
        fd.append('employeeUid', uid);
        // Use the raw axios instance (AppService.post drops the config arg, so its default
        // application/json content-type would be sent for the multipart upload).
        // eslint-disable-next-line no-await-in-loop
        const axiosResp = await AppService.api.post(FILE_UPLOAD_ENDPOINT, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        const body = axiosResp?.data ?? axiosResp;
        const fp = body?.data?.filePath || body?.filePath || body?.data?.path || body?.path;
        if (!fp) throw new Error('No filePath in upload response');
        setSubs((prev) => prev.map((s) => (s.id === subId ? {
          ...s,
          files: s.files.map((f) => (f.id === tempId
            ? { id: tempId, filePath: fp, fileName: file.name, contentType: file.type || null, fileSize: file.size || null }
            : f)),
        } : s)));
      } catch (e) {
        setSubs((prev) => prev.map((s) => (s.id === subId ? { ...s, files: s.files.filter((f) => f.id !== tempId) } : s)));
        setError(`Failed to upload ${file.name}.`);
      }
    }
  };

  const anyUploading = subs.some((s) => s.files.some((f) => f.uploading));

  const handleSave = () => {
    // A certificate must have both a name and at least one uploaded file.
    const invalid = subs.find((s) => {
      const hasFiles = s.files.some((f) => f.filePath);
      const hasTitle = (s.title || '').trim().length > 0;
      return (hasFiles && !hasTitle) || (hasTitle && !hasFiles);
    });
    if (invalid) { setError('Each certificate needs both a name and at least one file.'); return; }

    const cleaned = subs
      .map((s) => ({
        title: (s.title || '').trim(),
        files: s.files.filter((f) => f.filePath).map((f) => ({ filePath: f.filePath, fileName: f.fileName, contentType: f.contentType || null, fileSize: f.fileSize || null })),
      }))
      .filter((s) => s.title && s.files.length > 0);

    if (cleaned.length === 0 && existingRefs.length === 0) {
      onChange({ target: { name: field.name, value: '' } });
      setOpen(false);
      return;
    }

    onChange({ target: { name: field.name, value: {
      isMultiCertificate: true,
      documentType: fieldCode,
      backendDocumentType: backendType,
      existingRefs,
      subs: cleaned,
    } } });
    setOpen(false);
  };

  return (
    <Box>
      <Typography variant="body2" sx={{ display: 'block', mb: 0.75, fontWeight: 500, color: 'text.primary' }}>
        {label}
      </Typography>
      {displayDocs.length > 0 && (
        <Box sx={{ border: '1px solid', borderColor: 'grey.300', borderRadius: 1, p: 1, mb: 1 }}>
          <Stack spacing={0.5}>
            {displayDocs.map((d, i) => (
              <Box key={d.id || d.documentId || `${d.filePath}-${i}`} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {d.isPrimary
                  ? <StarIcon fontSize="small" sx={{ color: 'warning.main' }} />
                  : <InsertDriveFileOutlinedIcon fontSize="small" color="action" />}
                <Typography variant="body2" sx={{ flex: 1, wordBreak: 'break-all' }}>
                  {d.fileName || 'Document'}{d.isPrimary ? ' (Primary)' : ''}
                </Typography>
                {d.filePath && (
                  <WDButton variant="outlined" size="small" onClick={() => viewDoc(d.filePath, d.fileName)} sx={{ minWidth: '56px', height: '28px', fontSize: '0.75rem' }}>
                    VIEW
                  </WDButton>
                )}
              </Box>
            ))}
          </Stack>
        </Box>
      )}
      <Button
        variant="contained"
        size="small"
        onClick={openDialog}
        disabled={disabled}
        startIcon={<AttachFileIcon />}
        sx={{ textTransform: 'none', fontWeight: 600 }}
      >
        {displayDocs.length > 0 ? 'Add / Manage Certificates' : 'Add Certificates'}
      </Button>

      <Dialog open={open} onClose={() => !anyUploading && setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{label} — Certificates</DialogTitle>
        <DialogContent dividers>
          {existingRefs.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {existingRefs.length} certificate file(s) already on the employee profile will be kept.
            </Typography>
          )}
          <Stack spacing={2}>
            {subs.map((s) => (
              <Box key={s.id} sx={{ border: '1px solid', borderColor: 'grey.300', borderRadius: 1, p: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField
                    fullWidth size="small" label="Certificate Name" required
                    placeholder="e.g. Bachelor Degree"
                    value={s.title}
                    onChange={(e) => setSubTitle(s.id, e.target.value)}
                  />
                  <IconButton aria-label="remove certificate" onClick={() => removeSub(s.id)} disabled={subs.length <= 1}>
                    <DeleteOutlineIcon />
                  </IconButton>
                </Box>
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Button component="label" size="small" variant="contained" startIcon={<AttachFileIcon />}>
                    Add Files
                    <input hidden type="file" multiple onChange={(e) => { onSubFiles(s.id, e.target.files); e.target.value = ''; }} />
                  </Button>
                  {s.files.length === 0 && <Typography variant="caption" color="text.secondary">No files yet</Typography>}
                </Box>
                <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
                  {s.files.map((f) => (
                    <Chip
                      key={f.id}
                      size="small"
                      icon={f.uploading ? <CircularProgress size={12} /> : undefined}
                      label={f.fileName}
                      onDelete={f.uploading ? undefined : () => removeFile(s.id, f.id)}
                    />
                  ))}
                </Stack>
              </Box>
            ))}
          </Stack>
          <Button sx={{ mt: 2 }} size="small" startIcon={<AddIcon />} onClick={addSub}>
            Add Another Certificate
          </Button>
          {error && <FormHelperText error sx={{ mt: 1 }}>{error}</FormHelperText>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={anyUploading}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={anyUploading}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

MultiCertificateField.propTypes = {
  field: PropTypes.object.isRequired,
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default MultiCertificateField;
